import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, Search, Plus, Trash2, Save, X, ChevronDown, Check, AlertTriangle } from 'lucide-react';
import { apiService } from '../../services/api';
import { Recipe, RecipeIngredient, Producto } from '../../types';
import { PRODUCT_CATEGORY_MAP, getProductCategory, getAllCategories, ProductCategory } from '../../utils/productCategories';

const Recipes: React.FC = () => {
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [inventoryProducts, setInventoryProducts] = useState<Producto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Left panel state
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<ProductCategory | 'all'>('all');
  const [selectedItem, setSelectedItem] = useState<string | null>(null);

  // Right panel editor state
  const [editingRecipe, setEditingRecipe] = useState<{
    menuItemName: string;
    category: string;
    ingredients: RecipeIngredient[];
    isActive: boolean;
  } | null>(null);

  // All menu items from productCategories
  const allMenuItems = Object.entries(PRODUCT_CATEGORY_MAP).map(([name, category]) => ({
    name,
    category: category as string,
  })).sort((a, b) => a.name.localeCompare(b.name));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [recipesData, inventoryData] = await Promise.all([
        apiService.getAllRecipes(),
        apiService.getAllInventory(),
      ]);
      setRecipes(recipesData);
      setInventoryProducts(inventoryData);
    } catch (error) {
      console.error('Error loading recipes data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const recipeMap = new Map(recipes.map(r => [r.menuItemName, r]));

  const filteredMenuItems = allMenuItems.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const totalRecipes = recipes.length;
  const totalMenuItems = allMenuItems.length;
  const coveragePercent = totalMenuItems > 0 ? Math.round((totalRecipes / totalMenuItems) * 100) : 0;
  const unconnectedCount = totalMenuItems - totalRecipes;

  const handleSelectItem = (name: string) => {
    setSelectedItem(name);
    const existing = recipeMap.get(name);
    if (existing) {
      setEditingRecipe({
        menuItemName: existing.menuItemName,
        category: existing.category,
        ingredients: [...existing.ingredients],
        isActive: existing.isActive,
      });
    } else {
      setEditingRecipe({
        menuItemName: name,
        category: getProductCategory(name) as string,
        ingredients: [],
        isActive: true,
      });
    }
  };

  const handleAddIngredient = () => {
    if (!editingRecipe) return;
    setEditingRecipe({
      ...editingRecipe,
      ingredients: [
        ...editingRecipe.ingredients,
        { inventoryProductId: '', inventoryProductName: '', quantity: 0, unit: 'g' },
      ],
    });
  };

  const handleRemoveIngredient = (index: number) => {
    if (!editingRecipe) return;
    const updated = [...editingRecipe.ingredients];
    updated.splice(index, 1);
    setEditingRecipe({ ...editingRecipe, ingredients: updated });
  };

  const handleIngredientChange = (index: number, field: keyof RecipeIngredient, value: string | number) => {
    if (!editingRecipe) return;
    const updated = [...editingRecipe.ingredients];
    if (field === 'inventoryProductId') {
      const product = inventoryProducts.find(p => p.id === value);
      updated[index] = {
        ...updated[index],
        inventoryProductId: value as string,
        inventoryProductName: product?.producto || '',
      };
    } else {
      updated[index] = { ...updated[index], [field]: value };
    }
    setEditingRecipe({ ...editingRecipe, ingredients: updated });
  };

  const handleSave = async () => {
    if (!editingRecipe || editingRecipe.ingredients.length === 0) return;

    // Validate all ingredients have a product and quantity
    const valid = editingRecipe.ingredients.every(
      ing => ing.inventoryProductId && ing.quantity > 0
    );
    if (!valid) return;

    setSaving(true);
    try {
      await apiService.createRecipe(editingRecipe);
      await fetchData();
    } catch (error) {
      console.error('Error saving recipe:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    if (!window.confirm(`Eliminar receta para "${selectedItem}"?`)) return;

    try {
      await apiService.deleteRecipe(selectedItem);
      setEditingRecipe(null);
      setSelectedItem(null);
      await fetchData();
    } catch (error) {
      console.error('Error deleting recipe:', error);
    }
  };

  const unitOptions = ['g', 'kg', 'ml', 'L', 'pz', 'oz', 'taza', 'cda', 'cdta'];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
          <p className="mt-4 text-medium-gray">Cargando recetas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-dark-text font-heading flex items-center">
            <BookOpen size={28} className="mr-3 text-accent" />
            Recetas
          </h2>
          <p className="text-sm text-medium-gray mt-1">
            Conecta productos del menu con ingredientes del inventario
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20">
          <p className="text-sm text-medium-gray">Total Recetas</p>
          <p className="text-2xl font-bold text-dark-text">{totalRecipes}</p>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20">
          <p className="text-sm text-medium-gray">Cobertura</p>
          <div className="flex items-center">
            <p className="text-2xl font-bold text-dark-text">{coveragePercent}%</p>
            <div className="ml-3 flex-1">
              <div className="w-full bg-light-gray/20 rounded-full h-2">
                <div
                  className="bg-accent rounded-full h-2 transition-all"
                  style={{ width: `${coveragePercent}%` }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm border border-light-gray/20">
          <p className="text-sm text-medium-gray">Sin Receta</p>
          <p className="text-2xl font-bold text-red-500">{unconnectedCount}</p>
        </div>
      </div>

      {/* Two-Panel Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Panel: Menu Items List */}
        <div className="lg:col-span-1 bg-white rounded-lg shadow-sm border border-light-gray/20 overflow-hidden">
          <div className="p-4 border-b border-light-gray/20">
            <h3 className="text-lg font-semibold text-dark-text mb-3">Productos del Menu</h3>
            <div className="relative mb-3">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-medium-gray" />
              <input
                type="text"
                placeholder="Buscar producto..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-light-gray/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-sm"
              />
            </div>
            <div className="relative">
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as ProductCategory | 'all')}
                className="w-full px-3 py-2 border border-light-gray/30 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-accent appearance-none pr-10"
              >
                <option value="all">Todas las categorias</option>
                {getAllCategories().map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-medium-gray pointer-events-none" />
            </div>
          </div>
          <div className="overflow-y-auto max-h-[600px]">
            {filteredMenuItems.map(item => {
              const hasRecipe = recipeMap.has(item.name);
              const recipe = recipeMap.get(item.name);
              const isSelected = selectedItem === item.name;
              return (
                <button
                  key={item.name}
                  onClick={() => handleSelectItem(item.name)}
                  className={`w-full text-left px-4 py-3 border-b border-light-gray/10 transition-colors ${
                    isSelected ? 'bg-accent/10 border-l-4 border-l-accent' : 'hover:bg-cream/50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-dark-text truncate">
                        {item.name}
                      </div>
                      <div className="text-xs text-medium-gray">{item.category}</div>
                    </div>
                    <div className="flex items-center ml-2 flex-shrink-0">
                      {hasRecipe ? (
                        <span className="flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                          <Check size={12} className="mr-1" />
                          {recipe!.ingredients.length}
                        </span>
                      ) : (
                        <span className="flex items-center text-xs text-red-500 bg-red-50 px-2 py-1 rounded-full">
                          <AlertTriangle size={12} className="mr-1" />
                          Sin
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Panel: Recipe Editor */}
        <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-light-gray/20">
          {editingRecipe ? (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-dark-text">{editingRecipe.menuItemName}</h3>
                  <p className="text-sm text-medium-gray">{editingRecipe.category}</p>
                </div>
                <div className="flex items-center space-x-2">
                  {recipeMap.has(editingRecipe.menuItemName) && (
                    <button
                      onClick={handleDelete}
                      className="flex items-center px-3 py-2 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <Trash2 size={16} className="mr-1" />
                      Eliminar
                    </button>
                  )}
                  <button
                    onClick={() => { setEditingRecipe(null); setSelectedItem(null); }}
                    className="p-2 text-medium-gray hover:text-dark-text transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Ingredients */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-md font-semibold text-dark-text">Ingredientes</h4>
                  <button
                    onClick={handleAddIngredient}
                    className="flex items-center px-3 py-2 text-sm bg-accent text-cream rounded-lg hover:bg-warm-brown transition-colors"
                  >
                    <Plus size={16} className="mr-1" />
                    Agregar
                  </button>
                </div>

                {editingRecipe.ingredients.length === 0 ? (
                  <div className="text-center py-8 text-medium-gray border-2 border-dashed border-light-gray/30 rounded-lg">
                    <BookOpen size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Sin ingredientes. Haz clic en "Agregar" para comenzar.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Header row */}
                    <div className="grid grid-cols-12 gap-3 px-2 text-xs font-medium text-medium-gray uppercase">
                      <div className="col-span-5">Producto Inventario</div>
                      <div className="col-span-3">Cantidad</div>
                      <div className="col-span-3">Unidad</div>
                      <div className="col-span-1"></div>
                    </div>
                    {editingRecipe.ingredients.map((ingredient, index) => (
                      <div key={index} className="grid grid-cols-12 gap-3 items-center bg-cream/30 p-3 rounded-lg">
                        <div className="col-span-5">
                          <select
                            value={ingredient.inventoryProductId}
                            onChange={(e) => handleIngredientChange(index, 'inventoryProductId', e.target.value)}
                            className="w-full px-3 py-2 border border-light-gray/30 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-accent appearance-none"
                          >
                            <option value="">Seleccionar...</option>
                            {inventoryProducts.map(p => (
                              <option key={p.id} value={p.id}>
                                {p.producto} ({p.stockFinal} {p.unidad})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-3">
                          <input
                            type="number"
                            min="0"
                            step="0.1"
                            value={ingredient.quantity || ''}
                            onChange={(e) => handleIngredientChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                            placeholder="Cant."
                            className="w-full px-3 py-2 border border-light-gray/30 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
                          />
                        </div>
                        <div className="col-span-3">
                          <select
                            value={ingredient.unit}
                            onChange={(e) => handleIngredientChange(index, 'unit', e.target.value)}
                            className="w-full px-3 py-2 border border-light-gray/30 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-accent appearance-none"
                          >
                            {unitOptions.map(u => (
                              <option key={u} value={u}>{u}</option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-1 flex justify-center">
                          <button
                            onClick={() => handleRemoveIngredient(index)}
                            className="p-1 text-red-400 hover:text-red-600 transition-colors"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Save Button */}
              {editingRecipe.ingredients.length > 0 && (
                <div className="flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={saving || editingRecipe.ingredients.some(i => !i.inventoryProductId || i.quantity <= 0)}
                    className="flex items-center px-6 py-3 bg-accent text-cream rounded-lg hover:bg-warm-brown transition-colors disabled:opacity-50"
                  >
                    <Save size={18} className="mr-2" />
                    {saving ? 'Guardando...' : 'Guardar Receta'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center h-full min-h-[400px] text-center">
              <div className="text-medium-gray">
                <BookOpen size={48} className="mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">Selecciona un producto del menu</p>
                <p className="text-sm mt-1">para crear o editar su receta</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Recipes;
